import type { Dictionary } from './core.js';

export const en: Dictionary = {
  'app.title': 'Duren',
  'app.cardsCredit':
    'Cards: SVG Playing Cards by Daniel S. Fowler (tekeye.uk), CC0 / public domain.',

  'mode.local': 'On this device',
  'mode.online': 'Online room',
  'mode.localLede': 'Play against bots, or against each other on one device.',
  'mode.onlineLede':
    'Play with other people. Create a room and share the code — no account, nothing to install.',

  'conn.online': 'connected',
  'conn.connecting': 'connecting…',
  'conn.offline': 'disconnected',

  'field.players': 'Players',
  'field.deck': 'Deck',
  'field.seed': 'Seed',
  'field.seedHint': 'The same seed deals the same cards — every game is reproducible.',
  'field.name': 'Your name',
  'field.roomCode': 'Room code',
  'field.seat': 'Seat {n}',
  'player.seat': 'Player {n}',
  'field.language': 'Language',
  'deck.36': '36 cards',
  'deck.52': '52 cards',
  'seat.human': 'Human',
  'seat.soon': ' (coming soon)',

  'action.newGame': 'New game',
  'action.undo': 'Undo',
  'action.createRoom': 'Create room',
  'action.join': 'Join',
  'action.watch': 'Watch',
  'action.start': 'Start game',
  'action.rematch': 'Rematch',
  'action.leave': 'Leave room',
  'action.copyLink': 'Copy link',
  'action.copied': 'Copied',
  'action.sitHere': 'Sit here',
  'action.removeBot': 'Remove',
  'action.send': 'Send',
  'action.take': 'I take',
  'action.pass': 'Done / beaten',
  'action.transfer': 'Transfer {card}',
  'action.transferReveal': 'Transfer {card} (show)',
  'action.switchTo': 'Switch to {name}',

  'board.trump': 'Trump',
  'board.trumpIs': 'Trump: {suit}',
  'board.bout': 'Bout {n}',
  'board.deck': 'Deck {n}',
  'board.discard': 'Discarded {n}',
  'board.deckEmpty': 'The deck is empty',
  'board.table': 'Table',
  'board.trumpCard': 'Trump card at the bottom of the deck',
  'board.attacks': '{name} attacks.',
  'board.gameOver': 'The game is over.',
  'board.unbeatenHint': 'Unbeaten — click to choose what to defend',
  'board.noCards': 'no cards',
  'board.switchHint': 'Switch to this player',
  'board.notYours': 'You are not playing this seat',
  'board.manyActors': 'More than one player can act right now.',
  'board.waiting': 'Waiting for the others…',
  'board.cards': { one: '{count} card', other: '{count} cards' },

  'settings.title': 'Game settings',
  'settings.botSpeed': 'Bot pause',
  'settings.botSpeed.hint': 'How long a bot waits before playing a card. Zero means instantly.',
  'settings.seconds': '{n} s',
  'settings.holdForThrowIn': 'Wait for me when I could throw in',
  'settings.holdForThrowIn.hint': 'Bots stop until you play or press \u201cDone\u201d. Without it somebody can pile on before you have noticed you had a card.',
  'settings.showLog': 'Show the transcript',
  'settings.showLog.hint': 'A side column of who played what.',
  'board.yourThrowIn': 'You can throw in \u2014 play a card or press \u201cDone\u201d.',
  'settings.showStatus': 'Show the game info',
  'settings.showStatus.hint': 'Trump, bout, deck and discard — right above your hand.',
  'setup.title': 'Settings and new game',
  'card.back': 'Card back',
  'card.deckOf': '{count} cards in the deck',

  'role.bot': 'bot',
  'role.away': 'disconnected',
  'role.substituted': 'a bot is playing for them',
  'role.attacks': 'attacking',
  'role.defends': 'defending',
  'role.takes': 'taking',
  'role.out': 'out of the game',
  'role.passed': 'passed',

  'suit.clubs': 'Clubs',
  'suit.diamonds': 'Diamonds',
  'suit.hearts': 'Hearts',
  'suit.spades': 'Spades',

  'banner.stalemate': 'Stalemate — the cards just went round in circles, nobody is the fool.',
  'banner.draw': 'A draw — everybody ran out at the same time.',
  'rules.teams': 'Teams 2v2',
  'rules.teams.hint': 'Four players, partners opposite. Partners never attack or transfer to each other.',
  'team.name': 'Team {n}',
  'banner.teamLost': '{team} loses.',
  'banner.durak': '{name} is the duren.',

  'log.title': 'What happened',
  'log.dealt': 'Dealt. Trump card: {card}.',
  'log.attack': '{name} attacks with {card}.',
  'log.throwIn': '{name} throws in {card}.',
  'log.defend': '{name} beats it with {card}.',
  'log.transfer': '{name} transfers {card} to {target}.',
  'log.transferReveal': '{name} shows {card} and transfers the attack to {target}.',
  'log.takeDeclared': '{name} takes.',
  'log.take': '{name} picks up {cards}.',
  'log.pass': '{name} passes.',
  'log.bito': 'Beaten: {cards}.',
  'log.draw': '{name} drew {count} cards.',
  'log.trumpTaken': '{name} takes the trump card {card} — the deck is empty.',
  'log.out': '{name} is out of cards.',
  'log.stalemate': 'Stalemate — the game stopped going anywhere.',
  'log.drawGame': 'A draw — nobody is the fool.',
  'log.durak': 'Duren: {name}.',

  'bots.hint.none': 'Every seat is a person at this device.',
  'bots.1.name': 'Beginner',
  'bots.1.blurb': 'Plays the cheapest legal card and hoards trumps. Remembers nothing.',
  'bots.2.name': 'Improver',
  'bots.2.blurb': 'Tracks the phase of the game, spends trumps carefully, weighs taking.',
  'bots.3.name': 'Counter',
  'bots.3.blurb': 'Counts the discard pile and works out what the opponent can beat.',
  'bots.4.name': 'Master',
  'bots.4.blurb': "Models the opponent's hand and bluffs on purpose.",

  'lobby.room': 'Room',
  'lobby.shareHint': 'Send this code or link to anyone joining. No sign-up needed.',
  'lobby.host': 'host',
  'lobby.you': 'you',
  'lobby.rulesByHost': 'The host sets the rules.',
  'lobby.startByHost': 'The host starts the game.',
  'lobby.chatPlaceholder': 'Say something…',
  'lobby.chatLabel': 'Chat message',
  'lobby.addBot': '+ {name}',

  'rules.title': 'Rules',
  'rules.custom': 'custom',
  'rules.scope': 'Who may throw in',
  'rules.scope.all': 'Anyone',
  'rules.scope.neighbours': "Only the defender's neighbours",
  'rules.cap': 'Attack limit',
  'rules.cap.defenderHand': "The defender's hand",
  'rules.cap.unlimited': 'No limit',
  'rules.cap.fixed4': 'At most 4 cards',
  'rules.cap.fixed6': 'At most 6 cards',
  'rules.afterTake': 'Throwing in after "I take"',
  'rules.afterTake.sameAsAttack': 'Same limit as the attack',
  'rules.afterTake.defenderHandAtBoutStart': 'The hand they started the bout with',
  'rules.afterTake.unlimited': 'Up to the table ceiling',
  'rules.firstAttacker': 'Who opens',
  'rules.firstAttacker.lowestTrump': 'Lowest trump',
  'rules.firstAttacker.random': 'At random',
  'rules.transfer': 'Transfer (perevodnoy)',
  'rules.transfer.hint': 'The defender may pass the attack on with a card of the same rank.',
  'rules.chains': 'Chained transfers',
  'rules.chains.hint': 'The new defender may transfer onward too.',
  'rules.reveal': 'Transfer by showing a trump',
  'rules.reveal.hint': 'A trump of the same rank is only shown; it stays in hand.',
  'rules.mustBeat': 'The defender must beat if they can',
  'rules.mustBeat.hint':
    'Taking is only legal when nothing beats the attack. Changes the game more than it looks.',
  'rules.throwInAfterTake': 'Keep throwing in after "I take"',
  'rules.firstBoutCap': 'First bout capped',
  'rules.firstBoutCap.hint': 'The first attack of the game holds one card fewer than a hand.',
  'rules.trumpVisible': 'Trump card face up',
  'rules.trumpVisible.hint': 'The bottom card of the deck lies face up.',

  'preset.classic': 'Classic duren',
  'preset.classic.blurb': '36 cards, anyone may throw in, the defender may take at any time.',
  'preset.perevodnoy': 'With transfers',
  'preset.perevodnoy.blurb':
    'The defender may pass the attack to the next player with a matching rank.',
  'preset.strict': 'Strict',
  'preset.strict.blurb': 'Taking only when nothing beats the attack, first bout capped.',
  'preset.big': 'Big table',
  'preset.teams': 'Teams 2v2',
  'preset.teams.blurb': 'Four players, partners sitting opposite. Your partner never attacks you.',
  'preset.big.blurb': "52 cards for 5–6 players, only the defender's neighbours attack.",

  'problem.deck_too_small':
    '{deckSize} cards are not enough for {players} players — at least {needed} are needed to leave a trump card.',
  'problem.deck_barely_sufficient':
    'Very few cards are left after the deal; the endgame starts almost immediately.',
  'problem.players_out_of_range': 'The number of players must be between {min} and {max}.',
  'problem.hand_size_out_of_range': 'Unsupported hand size.',
  'problem.max_table_slots_out_of_range': 'Unsupported table limit.',
  'problem.first_bout_cap_meaningless': 'The first-bout cap means nothing with a hand this small.',
  'problem.attack_cap_out_of_range': 'Unsupported attack limit.',
  'problem.scope_has_no_effect':
    "At this table size everybody is the defender's neighbour — the setting changes nothing.",
  'problem.transfer_options_without_transfer':
    'The transfer sub-options do nothing while transfers themselves are off.',
  'problem.transfer_two_players':
    'Heads-up, a transferred attack bounces straight back at the attacker — the game gets wilder.',
  'problem.must_beat_all_changes_the_game':
    '"Must beat" removes the defender\'s choice and helps counting bots a great deal.',
  'problem.must_beat_all_with_unlimited_pile':
    'With unlimited throwing in after "I take", attackers can grind the defender\'s whole hand.',
  'problem.teams_need_four': '2v2 teams need exactly four players; there are {players}.',
  'problem.scope_ignored_in_teams': 'In teams the defender\u2019s neighbours are exactly the two opponents — the setting changes nothing.',
  'problem.not_enough_players': 'A game needs at least two — add a player or a bot.',

  'error.room_not_found': 'No room with that code. Check the code.',
  'error.room_full': 'The room is full.',
  'error.room_in_progress': 'A game is already running in that room.',
  'error.not_host': 'Only the host can change that.',
  'error.not_enough_players': 'A game needs at least two players.',
  'error.server_full': 'The server is full right now, try again shortly.',
  'error.seat_taken': 'That seat is taken.',
  'error.rate_limited': 'Too many actions at once, slow down a little.',
  'error.illegal_move': 'That move is against the rules.',
  'error.protocol_mismatch': 'This page is out of date — reload it (Ctrl+R).',
  'error.unknown': 'Error: {code}',

  // --- table design (skins, chairs, waiting room) ---
  'role.idle': 'waiting',
  'turn.you.attack': 'You · attacking',
  'turn.you.defend': 'You · defending',
  'turn.you.act': 'You · your move',
  'settings.appearance': 'Appearance',
  'settings.skin': 'Visual style',
  'settings.theme': 'Day / night mode',
  'skin.organic': 'Organic',
  'skin.modern': 'Modern',
  'skin.classic': 'Classic',
  'theme.light': 'Day',
  'theme.dark': 'Night',
  'entry.kicker': 'Welcome to the table',
  'entry.title': 'Play some duren.',
  'entry.botsKicker': 'Opponents',
  'entry.botsTitle': 'Bots you can seat at the table',
  'lobby.waitingTitle': 'Waiting for players',
  'lobby.seatsFilled': '{n} of {max} seats taken',
  'lobby.chat': 'Chat',
  'lobby.facts': 'Table settings',
  'lobby.invite': 'Invite',
  'lobby.seated': 'Seated',
  'lobby.free': 'Free seat',
  'lobby.freeHint': 'waiting for a player or a bot',
  'lobby.player': 'player',
  'lobby.fact.seats': 'Occupancy',
  'lobby.fact.code': 'Room code',
  'yes': 'yes',
  'no': 'no',

  // --- decks, sorting, hints ---
  'settings.cardTheme': 'Card deck',
  'cards.classic': 'Classic',
  'cards.minimal': 'Minimal',
  'settings.sortBy': 'Sort cards',
  'sort.suit': 'By suit',
  'sort.power': 'By strength',
  'settings.hints': 'Move hints',
  'settings.hints.hint': 'Dims the cards you cannot play right now. Off, the hand looks the same throughout and keeping track is on you.',
  'role.canThrowIn': 'can throw in',
};
